import type ArrayProxy from '@ember/array/proxy';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { subMinutes, fromUnixTime } from 'date-fns';
import { format } from 'date-fns-tz';
import type DeskModel from 'garaje/models/desk';
import type EmployeeModel from 'garaje/models/employee';
import type FloorModel from 'garaje/models/floor';
import type MapFeatureModel from 'garaje/models/map-feature';
import type NeighborhoodModel from 'garaje/models/neighborhood';
import type ReservationModel from 'garaje/models/reservation';
import { ResourceType, type AvailabilityStatus } from 'garaje/services/live-map';
import type LiveMapService from 'garaje/services/live-map';
import type StateService from 'garaje/services/state';

interface DeskPopupComponentArgs {
  feature: MapFeatureModel;
  desks: ArrayProxy<DeskModel>;
  reservations: ArrayProxy<ReservationModel>;
  employees: ArrayProxy<EmployeeModel>;
  selectedDate: Date;
  selectedTime?: number;
  selectedEndTime?: number;
  setNeighborhoodToView: (neighborhoodId: string) => void;
  currentUserReservation?: ReservationModel;
}

export default class DeskPopup extends Component<DeskPopupComponentArgs> {
  @service declare state: StateService;
  @service declare store: Store;
  @service declare liveMap: LiveMapService;

  @tracked showNeighborhoodPopup = false;

  get selectedDateFormatted(): string {
    const { selectedDate } = this.args;
    return format(subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate)), 'MMMM dd, yyyy');
  }

  get floor(): FloorModel | null {
    const desk = this.getDeskById(this.args.feature.externalId);
    if (!desk) return null;

    return this.store.peekRecord('floor', desk.belongsTo('floor').id());
  }

  get neighborhood(): NeighborhoodModel | null {
    const { feature } = this.args;
    const desk = this.getDeskById(feature.externalId);
    if (!desk) return null;

    const neighborhoodId = desk.belongsTo('neighborhood').id();
    if (neighborhoodId) {
      return this.store.peekRecord('neighborhood', neighborhoodId);
    }
    return null;
  }

  @action
  getReservationByDeskId(deskId: string): ReservationModel | undefined {
    return this.args.reservations.find((res) => res.belongsTo('desk').id() == deskId);
  }

  @action
  getDeskById(deskId: string): DeskModel | null {
    return this.store.peekRecord('desk', deskId);
  }

  @action
  getEmployeeByEmail(email: string): EmployeeModel | undefined {
    const employees = this.store.peekAll('employee');
    return employees.find((employee) => employee.email === email);
  }

  @action
  partialDayFormat(reservation: ReservationModel): string {
    const startTime = fromUnixTime(reservation.startTime);
    const formattedStartTime = format(subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)), 'h:mma');
    const endTime = fromUnixTime(reservation.endTime);
    const formattedEndTime = format(subMinutes(endTime, this.state.minutesBetweenTimezones(endTime)), 'h:mma');
    return `${formattedStartTime} - ${formattedEndTime}`;
  }

  @action
  showScheduleSharingDisabled(employee: EmployeeModel, desk: DeskModel): boolean {
    return (
      !(desk.assignedTo === employee.email && employee.belongsTo('user').id() === this.state.currentUser.id) &&
      !(this.args.currentUserReservation?.belongsTo('user').id() === employee.belongsTo('user').id()) &&
      (employee.hideFromEmployeeSchedule || !this.state.currentLocation.employeeScheduleEnabled)
    );
  }

  @action
  onNeighborhoodClick(neighborhoodId: string, e: Event): void {
    e.stopPropagation();
    this.args.setNeighborhoodToView(neighborhoodId);
    this.showNeighborhoodPopup = true;
  }

  @action
  getDeskAvailabilityStatus(deskId: string): AvailabilityStatus | null {
    const liveMapState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    return liveMapState?.status.availabilityStatus ?? null;
  }
}
